/**
 * AnalysisForm Component
 * 
 * Influencer analiz formunun UI bileşeni.
 * State ve logic HomePage.js'de kalır, bu sadece görünüm (presentation) katmanı.
 */

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Search, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";

// Wave effect for buttons
const buttonWaveStyles = `
  @keyframes buttonWave {
    0%, 100% { transform: scale(1); }
    25% { transform: scale(1.25); }
    50%, 100% { transform: scale(1); }
  }
  
  .button-wave-text {
    display: inline-flex;
    gap: 0;
  }
  
  .button-wave-text span {
    display: inline-block;
    animation: buttonWave 4s ease-in-out infinite;
  }
  
  .button-wave-text span:nth-child(1) { animation-delay: 0s; }
  .button-wave-text span:nth-child(2) { animation-delay: 0.05s; }
  .button-wave-text span:nth-child(3) { animation-delay: 0.1s; }
  .button-wave-text span:nth-child(4) { animation-delay: 0.15s; }
  .button-wave-text span:nth-child(5) { animation-delay: 0.2s; }
  .button-wave-text span:nth-child(6) { animation-delay: 0.25s; }
  .button-wave-text span:nth-child(7) { animation-delay: 0.3s; }
  .button-wave-text span:nth-child(8) { animation-delay: 0.35s; }
  .button-wave-text span:nth-child(9) { animation-delay: 0.4s; }
  .button-wave-text span:nth-child(10) { animation-delay: 0.45s; }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .animate-fade-in {
    animation: fadeIn 0.5s ease-in-out;
  }
`;

const AnalysisForm = ({
  // Form state
  username,
  setUsername,
  displayPayment,
  handlePaymentChange,
  handlePaymentKeyDown,
  currency,
  setCurrency,
  // Loading state
  loading,
  analysisComplete,
  // Actions
  handleAnalyze,
  // User info
  user,
  // Team mode (OPARA EKİP)
  talepKodu,
  setTalepKodu,
  talepKoduValidation,
  setTalepKoduValidation,
  validateTalepKoduHandler,
  validatingTalepKodu,
}) => {
  const { t, locale } = useTranslation();
  // Check if user is OPARA team member
  // v19.5: OPARA EKİP Talep Kodu bölümü kaldırıldı (kullanıcı isteği)
  const isOparaTeamMember = false; // Disabled - was: user && user.email && user.email.toLowerCase().endsWith('@personaajans.com');

  // Currency list - 20 most common currencies
  const currencies = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'TRY', symbol: '₺', name: 'Turkish Lira' },
    { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
    { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
    { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
    { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
    { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
    { code: 'MXN', symbol: 'Mex$', name: 'Mexican Peso' },
    { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
    { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
    { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
    { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
    { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  ];

  return (
    <Card className="mb-8 bg-gray-900 border-2 border-[#FFDD59]" data-testid="analysis-form-card">
      <CardHeader className="pb-3">
        {/* CardHeader artık boş - buton yukarı taşındı */}
      </CardHeader>
      
      <CardContent>
        {/* OPARA Team: Talep Kodu Input (Only for @personaajans.com) */}
        {isOparaTeamMember && (
          <div className="mb-6 p-4 bg-purple-900/30 border border-purple-500 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <div className="bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                {t('analysis.team_badge')}
              </div>
              <span className="text-sm text-purple-300 font-semibold">
                {t('analysis.team_title')}
              </span>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-purple-200 mb-2">
                  {t('analysis.team_label')}
                </label>
                <input
                  type="text"
                  value={talepKodu}
                  onChange={(e) => {
                    setTalepKodu(e.target.value);
                    setTalepKoduValidation(null);
                  }}
                  onBlur={() => validateTalepKoduHandler(talepKodu)}
                  placeholder={t('analysis.team_placeholder')}
                  className="w-full bg-gray-800 border border-purple-500 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-purple-400"
                  data-testid="talep-kodu-input"
                />
              </div>
              
              {validatingTalepKodu && (
                <div className="text-sm text-purple-300 animate-pulse">
                  {t('analysis.team_validating')}
                </div>
              )}
              
              {talepKoduValidation && (
                <div className={`p-3 rounded-lg border ${
                  talepKoduValidation.valid 
                    ? 'bg-green-900/30 border-green-500 text-green-200' 
                    : 'bg-red-900/30 border-red-500 text-red-200'
                }`}>
                  <p className="font-semibold mb-2">{talepKoduValidation.message}</p>
                  {talepKoduValidation.valid && (
                    <div className="mt-2 text-xs space-y-1">
                      <p>• {t('analysisform.talep_email')} {talepKoduValidation.customer_email}</p>
                      <p>• {t('analysisform.talep_influencer_count')} {talepKoduValidation.influencer_count}</p>
                      <p>• {t('analysisform.talep_status')} {talepKoduValidation.status}</p>
                      {talepKoduValidation.influencers && talepKoduValidation.influencers.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-green-600">
                          <p className="font-semibold mb-1">{t('analysisform.talep_requested')}</p>
                          {talepKoduValidation.influencers.map((inf, idx) => (
                            <p key={idx} className="ml-2">
                              {idx + 1}. <span className="font-mono text-green-300">@{inf.username}</span>
                              {inf.payment && <span className="text-yellow-300"> - {inf.payment.toLocaleString('en-US')}</span>}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {talepKoduValidation && talepKoduValidation.valid && (
                <div className="bg-yellow-900/30 border border-yellow-500 rounded-lg p-3">
                  <p className="text-xs text-yellow-200">
                    {t('analysis.team_warning').replace('{customer_name}', talepKoduValidation.customer_name)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Tek Kutucuk: Profil Analizi + Maliyet (Sol/Sağ) */}
        <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Sol: Kullanıcı Adı */}
            <div>
              <label className="block text-base font-medium text-[#FFDD59] mb-2">
                {t('analysis.username_label')}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">@</span>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t('analysis.username_placeholder')}
                  className="pl-8 bg-gray-800 border-gray-700 text-white"
                  data-testid="username-input"
                />
              </div>
            </div>

            {/* Sağ: Reel Ücreti (Opsiyonel) + Currency Dropdown */}
            <div>
              <label className="block text-base font-medium text-[#FFDD59] mb-2">
                {t('analysis.payment_label')}
              </label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={displayPayment}
                  onChange={handlePaymentChange}
                  onKeyDown={handlePaymentKeyDown}
                  placeholder={t('analysis.payment_placeholder')}
                  className="flex-1 bg-gray-800 border-gray-700 text-white"
                  data-testid="payment-input"
                />
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-24 bg-gray-800 border border-gray-700 rounded-md text-white text-sm px-2 focus:outline-none focus:ring-2 focus:ring-[#FFDD59]"
                  data-testid="currency-select"
                >
                  {currencies.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Analiz Butonu */}
        <div className="flex gap-3">
          <Button
            onClick={handleAnalyze}
            disabled={loading}
            className={`flex-1 px-8 text-base font-medium ${
              loading ? 'h-28 py-4' : 'h-12'
            } ${
              analysisComplete 
                ? 'bg-green-600 hover:bg-green-700 text-white' 
                : 'bg-orange-500 hover:bg-orange-600 text-white'
            }`}
            data-testid="analyze-button"
          >
            {loading ? (
              <div className="flex flex-col items-center justify-center w-full gap-3">
                <Loader2 className="h-8 w-8 animate-spin" />
                <div className="text-center animate-fade-in">
                  <span className="text-sm font-medium">{t('analysis.analyzing')}</span>
                </div>
              </div>
            ) : analysisComplete ? (
              <>
                {t('analysis.complete')}
              </>
            ) : (
              <>
                <style>{buttonWaveStyles}</style>
                <Search className="mr-2 h-5 w-5" />
                <span className="button-wave-text">
                  {t('analysis.analyze_btn').split('').map((char, i) => (
                    <span key={i}>{char === ' ' ? '\u00A0' : char}</span>
                  ))}
                </span>
              </>
            )}
          </Button>
        </div>

        {/* Global Disclaimer - Dynamic by Locale */}
        <div className="mt-3 text-center">
          <p className="text-xs text-muted-foreground italic">
            {t('analysis.disclaimer_new')}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default AnalysisForm;
